import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail, Phone, Building2, Briefcase, Sparkles, Send, Loader2, Pause, Play, X, ExternalLink, Calendar,
} from "lucide-react";
import { api } from "../../lib/api";

function initials(name) {
  return (name || "").trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join("");
}

function fmt(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

const SOURCE_LABELS = {
  contact_form: "Contact Form",
  onboarding_dialog: "AI Onboarding",
};

const STATUS_DOT = {
  sent: "bg-emerald-400",
  skipped: "bg-white/30",
  failed: "bg-red-400",
};

export function CustomerDetailDialog({ lead, onClose, onChanged }) {
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [running, setRunning] = useState(false);
  const [current, setCurrent] = useState(lead);

  useEffect(() => {
    setCurrent(lead);
  }, [lead]);

  useEffect(() => {
    if (!lead) return;
    setLoadingHistory(true);
    api
      .get(`/admin/followups/${lead.id}/history`)
      .then((res) => setHistory(res.data))
      .catch(() => setHistory([]))
      .finally(() => setLoadingHistory(false));
  }, [lead]);

  const runNow = async () => {
    if (!current || running) return;
    setRunning(true);
    try {
      await api.post(`/admin/followups/${current.id}/run-now`);
      const [historyRes] = await Promise.all([api.get(`/admin/followups/${current.id}/history`)]);
      setHistory(historyRes.data);
      setCurrent((c) => ({ ...c, follow_up_count: c.follow_up_count + 1 }));
      onChanged?.();
    } catch {
      // leave state as-is
    } finally {
      setRunning(false);
    }
  };

  const toggleEnabled = async () => {
    if (!current) return;
    const next = !current.follow_up_enabled;
    setCurrent((c) => ({ ...c, follow_up_enabled: next }));
    await api.patch(`/admin/leads/${current.id}`, { followUpEnabled: next }).catch(() => {});
    onChanged?.();
  };

  const pkg = current?.recommended_package;

  return (
    <Dialog.Root open={!!lead} onOpenChange={(v) => !v && onClose()}>
      <AnimatePresence>
        {lead ? (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
              />
            </Dialog.Overlay>
            <Dialog.Content asChild forceMount aria-describedby="customer-detail-desc">
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                <motion.div
                  className="w-full max-w-2xl max-h-[90dvh] overflow-y-auto overscroll-contain kb-glass rounded-2xl bg-[#0a1330] shadow-2xl relative"
                  initial={{ opacity: 0, y: 16, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.98 }}
                  transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                  data-testid="customer-detail-dialog"
                >
                  <Dialog.Close
                    className="absolute right-5 top-5 z-10 text-white/50 hover:text-[#d4a840] transition-colors"
                    data-testid="customer-detail-close"
                  >
                    <X size={18} />
                  </Dialog.Close>

                  <div className="p-6 sm:p-7">
                    <div className="flex items-start gap-4">
                      <div className="h-12 w-12 rounded-full bg-[#d4a840]/15 border border-[#d4a840]/40 flex items-center justify-center text-base font-semibold text-[#d4a840] shrink-0">
                        {initials(current?.name)}
                      </div>
                      <div className="min-w-0">
                        <Dialog.Title className="font-display text-2xl font-bold text-white leading-snug">
                          {current?.name}
                        </Dialog.Title>
                        <Dialog.Description id="customer-detail-desc" className="text-xs text-white/50 mt-1">
                          {SOURCE_LABELS[current?.source] || current?.source} · Enquired {fmt(current?.created_at)}
                        </Dialog.Description>
                      </div>
                    </div>

                    <div className="mt-5 grid sm:grid-cols-2 gap-3">
                      {current?.email ? (
                        <div className="flex items-center gap-2.5 text-sm text-white/80">
                          <Mail size={14} className="text-[#d4a840] shrink-0" /> {current.email}
                        </div>
                      ) : null}
                      {current?.phone ? (
                        <div className="flex items-center gap-2.5 text-sm text-white/80">
                          <Phone size={14} className="text-[#d4a840] shrink-0" /> {current.phone}
                        </div>
                      ) : null}
                      {current?.company ? (
                        <div className="flex items-center gap-2.5 text-sm text-white/80">
                          <Building2 size={14} className="text-[#d4a840] shrink-0" /> {current.company}
                        </div>
                      ) : null}
                      {current?.business_type ? (
                        <div className="flex items-center gap-2.5 text-sm text-white/80">
                          <Briefcase size={14} className="text-[#d4a840] shrink-0" /> {current.business_type}
                        </div>
                      ) : null}
                    </div>

                    {pkg ? (
                      <div className="mt-5 rounded-xl border border-[#d4a840]/20 bg-[#d4a840]/[0.04] p-4">
                        <div className="flex items-center gap-2 text-sm font-semibold text-white">
                          <Sparkles size={14} className="text-[#d4a840]" /> {pkg.title}
                        </div>
                        {current?.ai_note ? <p className="text-xs text-white/60 mt-1.5">{current.ai_note}</p> : null}
                        <ul className="mt-3 space-y-1.5">
                          {(pkg.certificates || []).map((c) => (
                            <li key={c.title} className="flex items-center justify-between gap-3 text-xs text-white/70">
                              <span>{c.title}</span>
                              <Link
                                to={`/services/${c.serviceSlug}`}
                                target="_blank"
                                className="text-[#d4a840]/80 hover:text-[#d4a840] shrink-0 flex items-center gap-1"
                              >
                                Details <ExternalLink size={10} />
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : current?.message ? (
                      <div className="mt-5 rounded-xl border border-white/10 bg-white/[0.02] p-4">
                        <div className="text-xs uppercase tracking-widest text-white/40 mb-1.5">Message</div>
                        <p className="text-sm text-white/70 leading-relaxed">{current.message}</p>
                      </div>
                    ) : null}

                    <div className="mt-6 flex items-center justify-between gap-4 flex-wrap border-t border-white/10 pt-5">
                      <div>
                        <div className="kb-label mb-1">Follow-ups</div>
                        <div className="flex items-center gap-2 text-sm text-white/70">
                          <span>{current?.follow_up_count ?? 0} / {current?.max_follow_ups ?? 4} sent</span>
                          {current?.follow_up_enabled ? (
                            <span className="flex items-center gap-1 text-white/50 text-xs">
                              <Calendar size={12} /> next {fmt(current?.next_follow_up_at)}
                            </span>
                          ) : (
                            <span className="text-white/40 text-xs">(paused)</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={toggleEnabled}
                          data-testid="customer-detail-toggle-followups"
                          className="p-2 rounded-lg border border-white/10 text-white/60 hover:text-white hover:border-[#d4a840]/40 transition-colors"
                          title={current?.follow_up_enabled ? "Pause follow-ups" : "Resume follow-ups"}
                        >
                          {current?.follow_up_enabled ? <Pause size={14} /> : <Play size={14} />}
                        </button>
                        <button
                          type="button"
                          onClick={runNow}
                          disabled={running}
                          data-testid="customer-detail-send-now"
                          className="kb-btn-ghost !py-2 !px-3 text-xs disabled:opacity-60"
                        >
                          {running ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                          Send now
                        </button>
                      </div>
                    </div>

                    <div className="mt-4">
                      <div className="kb-label mb-3">Follow-up history</div>
                      <div className="space-y-3" data-testid="customer-detail-history">
                        {loadingHistory ? (
                          <div className="flex justify-center py-8">
                            <Loader2 size={20} className="animate-spin text-[#d4a840]" />
                          </div>
                        ) : history.length === 0 ? (
                          <p className="text-white/40 text-sm py-6 text-center">No follow-ups sent yet.</p>
                        ) : (
                          history.map((h) => (
                            <div key={h.id} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                              <div className="flex items-center justify-between mb-2">
                                <span
                                  className={`text-xs font-medium capitalize flex items-center gap-1.5 ${
                                    h.status === "sent" ? "text-emerald-300" : h.status === "failed" ? "text-red-300" : "text-white/50"
                                  }`}
                                >
                                  <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[h.status] || "bg-white/30"}`} />
                                  {h.status}
                                </span>
                                <span className="text-xs text-white/40">{fmt(h.created_at)}</span>
                              </div>
                              <p className="text-sm text-white/75 leading-relaxed">{h.message}</p>
                              {h.error ? <p className="text-xs text-white/40 mt-2 italic">{h.error}</p> : null}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        ) : null}
      </AnimatePresence>
    </Dialog.Root>
  );
}
